import type { NomIcona } from '@/components/icones';
import type { Namespace } from '@/i18n';
import { compleix, type RequisitPermis } from '@/permisos/permisos';
import type { MeDto } from '@/api/types';

/**
 * Arquitectura d'informació del portal (redisseny BAtech): 5 seccions a la barra lateral.
 * Dins de cada secció, les sub-pantalles s'hi arriben amb pestanyes sota el títol.
 * Les rutes històriques es mantenen (enllaços profunds, proves, e2e); només s'agrupen.
 */
export type SeccioNav = 'centre' | 'activacions' | 'guardies' | 'analisi' | 'ajustos';

export interface ElementNav {
  clau: string;
  pantalla: string;
  ruta: string;
  seccio: SeccioNav;
  ns: Namespace;
  icona: NomIcona;
  permis?: RequisitPermis;
  /**
   * Pestanya de la secció on apareix (clau de `comu:nav.pestanyes`). Diversos elements poden
   * compartir pestanya: llavors es mostren com a sub-pestanyes (p. ex. Registre).
   */
  pestanya?: string;
}

export interface Seccio {
  clau: SeccioNav;
  icona: NomIcona;
  /** Prefixos de ruta que pertanyen a la secció (per marcar-la activa). */
  prefixos: string[];
  /** Ruta fixa de la secció; si no n'hi ha, s'entra a la primera pantalla permesa. */
  ruta?: string;
  /** Es mostra fixada a la part inferior de la barra lateral. */
  peu?: boolean;
}

const C = 'CONSULTAR' as const;

export const FUTURE_ROUTER = { v7_startTransition: true, v7_relativeSplatPath: true };

export const SECCIONS: Seccio[] = [
  { clau: 'centre', icona: 'square-grid', prefixos: ['/tauler'] },
  { clau: 'activacions', icona: 'exclamationmark-triangle', prefixos: ['/activacions'] },
  { clau: 'guardies', icona: 'calendar', prefixos: ['/planificacio'] },
  { clau: 'analisi', icona: 'chart-bar', prefixos: ['/analisi'] },
  { clau: 'ajustos', icona: 'sliders', prefixos: ['/admin'], ruta: '/admin', peu: true },
];

export const ELEMENTS_NAV: ElementNav[] = [
  {
    clau: 'tauler',
    icona: 'square-grid',
    pantalla: 'W-02',
    ruta: '/tauler',
    seccio: 'centre',
    ns: 'operacio',
    permis: { recurs: 'cai.activacions', tipus: C },
  },
  {
    clau: 'activacions',
    icona: 'exclamationmark-triangle',
    pantalla: 'W-03',
    ruta: '/activacions',
    seccio: 'activacions',
    ns: 'operacio',
    permis: { recurs: 'cai.activacions', tipus: C },
  },
  {
    clau: 'novaActivacio',
    icona: 'plus',
    pantalla: 'W-05',
    ruta: '/activacions/nova',
    seccio: 'activacions',
    ns: 'operacio',
    permis: { recurs: 'cai.activacions', tipus: 'ALTA' },
  },

  {
    clau: 'calendaris',
    icona: 'calendar',
    pantalla: 'W-10',
    ruta: '/planificacio/calendaris',
    seccio: 'guardies',
    pestanya: 'calendari',
    ns: 'planificacio',
    permis: { recurs: 'cai.calendaris', tipus: C },
  },
  {
    clau: 'cobertura',
    icona: 'shield-check',
    pantalla: 'W-15',
    ruta: '/planificacio/cobertura',
    seccio: 'guardies',
    pestanya: 'cobertura',
    ns: 'planificacio',
    permis: { recurs: 'cai.guardies', tipus: C },
  },
  {
    clau: 'canvisTorn',
    icona: 'arrow-swap',
    pantalla: 'W-14',
    ruta: '/planificacio/sollicituds',
    seccio: 'guardies',
    pestanya: 'sollicituds',
    ns: 'planificacio',
    permis: { recurs: 'cai.sollicituds', tipus: C },
  },
  {
    clau: 'experts',
    icona: 'person-badge',
    pantalla: 'W-16',
    ruta: '/planificacio/experts',
    seccio: 'guardies',
    pestanya: 'experts',
    ns: 'planificacio',
    permis: { recurs: 'cai.experts', tipus: C },
  },
  {
    clau: 'historial',
    icona: 'clock-arrow',
    pantalla: 'W-17',
    ruta: '/planificacio/historial',
    seccio: 'guardies',
    pestanya: 'historial',
    ns: 'planificacio',
    permis: { recurs: 'cai.calendaris', tipus: C },
  },

  {
    clau: 'indicadors',
    icona: 'chart-line',
    pantalla: 'W-40',
    ruta: '/analisi/indicadors',
    seccio: 'analisi',
    pestanya: 'indicadors',
    ns: 'informes',
    permis: { recurs: 'cai.informes', tipus: C },
  },
  {
    clau: 'informes',
    icona: 'chart-bar',
    pantalla: 'W-41',
    ruta: '/analisi/informes',
    seccio: 'analisi',
    pestanya: 'informes',
    ns: 'informes',
    permis: { recurs: 'cai.informes', tipus: C },
  },
  {
    clau: 'recurrents',
    icona: 'arrow-clockwise',
    pantalla: 'W-47',
    ruta: '/analisi/recurrents',
    seccio: 'analisi',
    pestanya: 'recurrents',
    ns: 'informes',
    permis: { recurs: 'cai.informes', tipus: C },
  },
  {
    clau: 'auditoria',
    icona: 'clipboard',
    pantalla: 'W-44',
    ruta: '/analisi/auditoria',
    seccio: 'analisi',
    pestanya: 'registre',
    ns: 'informes',
    permis: { recurs: 'cai.auditoria', tipus: C },
  },
  {
    clau: 'tracabilitat',
    icona: 'link',
    pantalla: 'W-45',
    ruta: '/analisi/tracabilitat',
    seccio: 'analisi',
    pestanya: 'registre',
    ns: 'informes',
    permis: { recurs: 'cai.auditoria', tipus: C },
  },

  {
    clau: 'usuaris',
    icona: 'person-3',
    pantalla: 'W-20',
    ruta: '/admin/usuaris',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.usuaris', tipus: C },
  },
  {
    clau: 'rols',
    icona: 'person-key',
    pantalla: 'W-22',
    ruta: '/admin/rols',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.rols', tipus: C },
  },
  {
    clau: 'usuarisInformar',
    icona: 'rectangle-stack',
    pantalla: 'W-23',
    ruta: '/admin/usuaris-informar',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.usuaris-informar', tipus: C },
  },
  {
    clau: 'arees',
    icona: 'building',
    pantalla: 'W-24',
    ruta: '/admin/arees',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.organitzacio', tipus: C },
  },
  {
    clau: 'zones',
    icona: 'map',
    pantalla: 'W-28',
    ruta: '/admin/zones',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.zones', tipus: C },
  },
  {
    clau: 'circuits',
    icona: 'arrow-up',
    pantalla: 'W-25',
    ruta: '/admin/circuits',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.circuits', tipus: C },
  },
  {
    clau: 'canals',
    icona: 'bell',
    pantalla: 'W-26',
    ruta: '/admin/canals',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.notificacions.politica', tipus: C },
  },
  {
    clau: 'plantilles',
    icona: 'template',
    pantalla: 'W-27',
    ruta: '/admin/plantilles',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.plantilles', tipus: C },
  },
  {
    clau: 'documentacio',
    icona: 'doc-text',
    pantalla: 'W-32',
    ruta: '/admin/documentacio',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.documents', tipus: C },
  },
  {
    clau: 'integracions',
    icona: 'cloud',
    pantalla: 'W-29',
    ruta: '/admin/integracions',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.integracions', tipus: C },
  },
  {
    clau: 'monitoratge',
    icona: 'waveform',
    pantalla: 'W-46',
    ruta: '/admin/monitoratge',
    seccio: 'ajustos',
    ns: 'informes',
    permis: { recurs: 'cai.monitoratge', tipus: C },
  },
  {
    clau: 'versions',
    icona: 'archive',
    pantalla: 'W-30',
    ruta: '/admin/versions',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.app-versions', tipus: C },
  },
  {
    clau: 'configuracio',
    icona: 'gear',
    pantalla: 'W-31',
    ruta: '/admin/configuracio',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.configuracio', tipus: C },
  },
];

export const DETALL_ACTIVACIO: ElementNav = {
  clau: 'detall',
  icona: 'exclamationmark-triangle',
  pantalla: 'W-04',
  ruta: '/activacions/:id',
  seccio: 'activacions',
  ns: 'operacio',
  permis: { recurs: 'cai.activacions', tipus: C },
};

export const EDITOR_CALENDARI: ElementNav = {
  clau: 'editorCalendari',
  icona: 'calendar',
  pantalla: 'W-11',
  ruta: '/planificacio/calendaris/:id',
  seccio: 'guardies',
  pestanya: 'calendari',
  ns: 'planificacio',
  permis: { recurs: 'cai.calendaris', tipus: C },
};

export const ROTACIO_CALENDARI: ElementNav = {
  clau: 'rotacioCalendari',
  icona: 'calendar',
  pantalla: 'W-13',
  ruta: '/planificacio/calendaris/:id/rotacio',
  seccio: 'guardies',
  pestanya: 'calendari',
  ns: 'planificacio',
  permis: { recurs: 'cai.calendaris', tipus: 'ALTA' },
};

/** Índex d'Ajustos: visible si es té accés almenys a una pantalla d'ajustos (ho comprova la pàgina). */
export const AJUSTOS: ElementNav = {
  clau: 'ajustos',
  icona: 'sliders',
  pantalla: 'AJ-00',
  ruta: '/admin',
  seccio: 'ajustos',
  ns: 'administracio',
};

const ADMIN_OCULTES: ElementNav[] = [
  {
    clau: 'detallUsuari',
    icona: 'person',
    pantalla: 'W-21',
    ruta: '/admin/usuaris/:id',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.usuaris', tipus: C },
  },
  {
    clau: 'importacio',
    icona: 'tray',
    pantalla: 'W-37',
    ruta: '/admin/usuaris-informar/importa',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.usuaris-informar', tipus: 'ALTA' },
  },
  {
    clau: 'politicaFitxers',
    icona: 'lock',
    pantalla: 'W-35',
    ruta: '/admin/documentacio/politica',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.documents.politica', tipus: C },
  },
  {
    clau: 'detallDocument',
    icona: 'doc-text',
    pantalla: 'W-33',
    ruta: '/admin/documentacio/:id',
    seccio: 'ajustos',
    ns: 'administracio',
    permis: { recurs: 'cai.documents', tipus: C },
  },
];

const ANALISI_OCULTES: ElementNav[] = [
  {
    clau: 'generador',
    icona: 'doc-export',
    pantalla: 'W-42',
    ruta: '/analisi/informes/nou',
    seccio: 'analisi',
    pestanya: 'informes',
    ns: 'informes',
    permis: { recurs: 'cai.informes', tipus: 'ALTA' },
  },
  {
    clau: 'informeActivacio',
    icona: 'doc-text',
    pantalla: 'W-43',
    ruta: '/analisi/informes/activacio/:id',
    seccio: 'analisi',
    pestanya: 'informes',
    ns: 'informes',
    permis: { recurs: 'cai.informes', tipus: C },
  },
  {
    clau: 'detallTracabilitat',
    icona: 'link',
    pantalla: 'W-45',
    ruta: '/analisi/tracabilitat/:id',
    seccio: 'analisi',
    pestanya: 'registre',
    ns: 'informes',
    permis: { recurs: 'cai.auditoria', tipus: C },
  },
];

export const RUTES_OCULTES: ElementNav[] = [
  DETALL_ACTIVACIO,
  EDITOR_CALENDARI,
  ROTACIO_CALENDARI,
  AJUSTOS,
  ...ADMIN_OCULTES,
  ...ANALISI_OCULTES,
];

/**
 * Rutes antigues que el redisseny integra en una altra pantalla: es redirigeixen perquè els
 * enllaços desats i les notificacions continuïn funcionant.
 * - Seguiment en directe (W-06) i Guàrdia actual (D-01) → Centre de control.
 * - Monitoratge (W-46) passa a Ajustos.
 */
export const REDIRECCIONS: { de: string; a: string }[] = [
  { de: '/seguiment', a: '/tauler' },
  { de: '/planificacio/guardia-actual', a: '/tauler' },
  { de: '/analisi/monitoratge', a: '/admin/monitoratge' },
];

const TOTS = [...ELEMENTS_NAV, ...RUTES_OCULTES];

/** Element (de qualsevol secció) per clau: dades de ruta i permís per als índexs (Ajustos). */
export function elementPerClau(clau: string): ElementNav | undefined {
  return TOTS.find((e) => e.clau === clau);
}

function coincideix(ruta: string, prefix: string): boolean {
  return ruta === prefix || ruta.startsWith(`${prefix}/`);
}

export function seccioDeRuta(ruta: string): SeccioNav | null {
  return SECCIONS.find((s) => s.prefixos.some((p) => coincideix(ruta, p)))?.clau ?? null;
}

/** Pantalles de la barra lateral (ELEMENTS_NAV) d'una secció a què l'usuari té accés. */
export function elementsVisibles(
  me: Pick<MeDto, 'permisos'> | null | undefined,
  seccio: SeccioNav,
) {
  return ELEMENTS_NAV.filter((e) => e.seccio === seccio && compleix(me, e.permis));
}

/** Destí de l'entrada de la barra lateral, o `null` si l'usuari no hi té cap pantalla. */
export function rutaSeccio(
  me: Pick<MeDto, 'permisos'> | null | undefined,
  s: Seccio,
): string | null {
  const visibles = elementsVisibles(me, s.clau);
  if (visibles.length === 0) return null;
  return s.ruta ?? visibles[0]?.ruta ?? null;
}

export interface PestanyaNav {
  clau: string;
  /** Primera pantalla permesa de la pestanya (destí de l'enllaç). */
  ruta: string;
  /** Pantalles permeses de la pestanya; si n'hi ha més d'una, són sub-pestanyes. */
  elements: ElementNav[];
}

/** Pestanyes visibles d'una secció, en l'ordre de declaració. */
export function pestanyesSeccio(
  me: Pick<MeDto, 'permisos'> | null | undefined,
  seccio: SeccioNav,
): PestanyaNav[] {
  const pestanyes: PestanyaNav[] = [];
  for (const e of elementsVisibles(me, seccio)) {
    if (!e.pestanya) continue;
    const existent = pestanyes.find((p) => p.clau === e.pestanya);
    if (existent) existent.elements.push(e);
    else pestanyes.push({ clau: e.pestanya, ruta: e.ruta, elements: [e] });
  }
  return pestanyes;
}

/** Pestanya activa: la que conté la ruta (o la seva sub-ruta: editor, generador, detall…). */
export function pestanyaActiva(ruta: string, seccio: SeccioNav): string | null {
  const candidats = TOTS.filter(
    (e) => e.seccio === seccio && e.pestanya && coincideix(ruta, e.ruta.replace(/\/:.*$/, '')),
  ).sort((a, b) => b.ruta.length - a.ruta.length);
  return candidats[0]?.pestanya ?? null;
}

/** Sub-pantalla activa dins d'una pestanya amb sub-pestanyes (p. ex. Auditoria / Traçabilitat). */
export function elementActiu(ruta: string, elements: ElementNav[]): ElementNav | undefined {
  return [...elements]
    .sort((a, b) => b.ruta.length - a.ruta.length)
    .find((e) => coincideix(ruta, e.ruta));
}
